import type { ReactiveSet } from '@solid-primitives/set'
import { Title } from '@solidjs/meta'
import { type Component, createSignal, Show } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Form from '~/components/Form'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useDebouncedSignal } from '~/hooks/useDebouncedSignal'
import { usePermissions } from '~/hooks/usePermissions'
import { useServerTable } from '~/hooks/useServerTable'
import { type Path, useNavigate } from '~/router'
import api from '~/services/api'

import { P } from '~/utils/permissions'

interface BuilderPage {
  id: string
  name: string
  slug: string
  status: 'draft' | 'published'
  nodes: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

interface BuilderPageListResult {
  items: BuilderPage[]
  meta: { total: number }
}

const BuilderPagesPage: Component = () => {
  const navigate = useNavigate()
  const { can } = usePermissions()
  const alert = useAlert()
  const toast = useToast()

  // Búsqueda server-side
  const [_search, debouncedSearch, setSearch] = useDebouncedSignal('')

  const table = useServerTable<BuilderPage>({
    fetcher: async params => {
      const { data } = await api.get('/pages', { params })
      return data?.data as BuilderPageListResult
    },
    params: () => {
      const q = debouncedSearch().trim()
      return q ? { name: `%${q}%` } : {}
    }
  })

  const [showModal, setShowModal] = createSignal(false)
  const [isSubmitting, setIsSubmitting] = createSignal(false)

  const openCreate = () => {
    setShowModal(true)
  }

  const handleSubmit = async (formData: FormData) => {
    setIsSubmitting(true)
    try {
      const name = formData.get('name') as string
      let slug = formData.get('slug') as string
      if (!slug)
        slug = name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')
      await api.post('/pages', { name, slug })
      toast.addToast({ title: 'Página creada', type: 'success' })
      setShowModal(false)
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar página',
      message: '¿Estás seguro de eliminar esta página?'
    })
    if (!ok) return
    try {
      await api.delete(`/pages/${id}`)
      toast.addToast({ title: 'Página eliminada', type: 'success' })
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    }
  }

  // ── Selección masiva ────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = createSignal<ReadonlySet<string>>(new Set())

  const handleSelectionChange = (selected: ReactiveSet<unknown>) => {
    const next = new Set<string>()
    for (const value of selected.values()) next.add(value as string)
    setSelectedIds(next)
  }

  const handleBulkDelete = async () => {
    const ids = [...selectedIds()]
    if (ids.length === 0) return
    const ok = await alert.confirmDelete({
      title: 'Eliminar páginas',
      message: `¿Eliminar ${ids.length} ${ids.length === 1 ? 'página' : 'páginas'}? Esta acción no se puede deshacer.`
    })
    if (!ok) return
    try {
      const { data } = await api.post('/pages/bulk-delete', { ids })
      const deleted = (data?.data as { deleted: number })?.deleted ?? 0
      toast.addToast({ title: `${deleted} ${deleted === 1 ? 'página eliminada' : 'páginas eliminadas'}`, type: 'success' })
      setSelectedIds(new Set<string>())
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    }
  }

  const handlePublish = async (id: string) => {
    try {
      await api.post(`/pages/${id}/publish`)
      toast.addToast({ title: 'Página publicada', type: 'success' })
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    }
  }

  const columns = [
    { header: 'Nombre', accessor: (p: BuilderPage) => p.name },
    { header: 'Slug', accessor: (p: BuilderPage) => p.slug },
    {
      header: 'Estado',
      accessor: (p: BuilderPage) => <Badge color={p.status === 'published' ? 'success' : 'warning'}>{p.status === 'published' ? 'Publicado' : 'Borrador'}</Badge>
    },
    {
      header: 'Acciones',
      accessor: (page: BuilderPage) => (
        <div class="flex gap-2">
          <Show when={page.status === 'draft' && can(P.pages.update)}>
            <Button size="sm" variant="soft" color="success" onClick={() => handlePublish(page.id)} tooltip="Publicar" icon="material-symbols-light:upload-outline" />
          </Show>
          <Show when={can(P.pages.update)}>
            <Button size="sm" variant="soft" color="default" onClick={() => navigate(`/admin/builder/paginas/${page.id}` as Path)} tooltip="Editar" icon="material-symbols-light:edit-outline" />
          </Show>
          <Show when={can(P.pages.delete)}>
            <Button size="sm" variant="soft" color="danger" onClick={() => handleDelete(page.id)} tooltip="Eliminar" icon="material-symbols-light:delete-outline" />
          </Show>
        </div>
      )
    }
  ]

  return (
    <>
      <Title>Builder - Páginas | Florez Jaramillo</Title>
      <Table<BuilderPage>
        title="Páginas"
        subtitle="Crea y gestiona páginas dinámicas"
        headerActions={
          <Show when={can(P.pages.create)}>
            <Button onClick={openCreate} prependIcon="material-symbols-light:add-circle-outline">
              Nueva Página
            </Button>
          </Show>
        }
        data={table.data()}
        columns={columns}
        keyField="id"
        loading={table.loading()}
        searchable
        searchPlaceholder="Buscar página..."
        onSearch={setSearch}
        serverSide
        selectable={can(P.pages.delete)}
        selectableColor="danger"
        onSelectionChange={handleSelectionChange}
        onDeleteSelected={() => void handleBulkDelete()}
        total={table.total()}
        page={table.page()}
        perPage={table.perPage()}
        onPageChange={table.onPageChange}
        onPerPageChange={table.onPerPageChange}
        emptyMessage="Aún no hay páginas"
      />

      <Modal isOpen={showModal()} onClose={() => setShowModal(false)}>
        <Modal.Header>
          <h2 class="text-xl font-bold text-foreground">Nueva Página</h2>
        </Modal.Header>
        <Modal.Body>
          <Form id="page-form" onSubmit={handleSubmit}>
            <div class="space-y-4">
              <Input name="name" label="Nombre" required placeholder="Ej: Mi Página" />
              <Input name="slug" label="Slug" placeholder="mi-pagina" />
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button type="button" variant="soft" color="default" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting()} form="page-form">
              {isSubmitting() ? 'Guardando...' : 'Crear'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <alert.AlertComponent />
    </>
  )
}

export default BuilderPagesPage
